import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import formAPI from "../../api/formApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import FormRow from "./FormRow";
import TableShimmer from "../../components/partials/TableShimmer";
import RowsPerPage from "../../components/partials/RowsPerPage";
import Pagination from "../../components/partials/Pagination";
import GlobalDeleteModal from "../../components/partials/GlobalDeleteModal";

function List() {
  const [tableLoading, setTableLoading] = useState(false);

  const [formData, setFormData] = useState([]); // State for store list
  const [pagination, setPagination] = useState({}); // State for pagination info
  const [searchParams, setSearchParams] = useSearchParams();

  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const query = React.useMemo(
    () => ({
      page: Number(searchParams.get("page")) || 1,
      limit: Number(searchParams.get("limit")) || 10,
      status: searchParams.get("status") || null,
      search: searchParams.get("search") || null,
    }),
    [searchParams],
  );

  const fetchFormData = useCallback(async () => {
    setTableLoading(true);
    setFormData([]);
    setPagination({});

    try {
      const res = await formAPI.getDetails(searchParams);
      if (res.code === 200) {
        const { pagination, forms } = res.data;

        setFormData(forms ?? []);
        setPagination(pagination ?? {});
      }
    } catch {
    } finally {
      setTableLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchFormData();
  }, [fetchFormData]);

  const searchTimeout = useRef();
  const handleSearch = (value) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      updateFilter("search", value);
    }, 400);
  };

  const updateFilter = useCallback(
    (key, value) => {
      const params = new URLSearchParams(searchParams);
      value ? params.set(key, value) : params.delete(key);

      if (key !== "page") {
        params.set("page", 1);
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams],
  );

  const handleOpenDeleteModal = (id) => {
    setDeleteId(id);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      setIsDeleting(true);

      const res = await formAPI.deleteItem(deleteId);

      if (res.code === 200) {
        showSuccess(res.message);
        fetchFormData();

        document.querySelector("#delete-modal .btn-close").click();
      } else {
        showError(res.message);
      }
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setIsDeleting(false);
      setDeleteId(null);
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Manage Customize Form</h4>
              <h6>
                View, search, and manage all form for customization available in
                your store.
              </h6>
            </div>
          </div>
          <div className="page-btn">
            <Link to="/form/add-new" className="btn btn-primary">
              <i className="ti ti-circle-plus me-1" />
              Add Form
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="card-body p-0">
            {pagination.page && (
              <div className="row">
                <div className="col-md-12">
                  <div className="countablepagination p-2">
                    <p>
                      Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                      {Math.min(
                        pagination.page * pagination.limit,
                        pagination.total,
                      )}{" "}
                      of {pagination.total} results
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="table-responsive">
              <table className="table">
                <thead className="thead-light">
                  <tr>
                    <th>S. No.</th>
                    <th>Name</th>
                    <th>Field Count</th>
                    <th>Submission</th>
                    <th>Strict</th>
                    <th>Created At</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    <TableShimmer rows={10} cols={7} />
                  ) : formData.length > 0 ? (
                    formData.map((form, index) => (
                      <FormRow
                        key={form._id}
                        form={form}
                        index={index}
                        page={query.page}
                        limit={query.limit}
                        onDelete={handleOpenDeleteModal}
                      />
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="text-center p-4 text-muted">
                        No Form Added,{" "}
                        <Link to="/form/add-new" className="text-primary">
                          Click Here{" "}
                        </Link>
                        to add new form
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {pagination.page && (
              <div className="row">
                <RowsPerPage
                  value={searchParams.get("limit") || "10"}
                  onChange={(val) => updateFilter("limit", val)}
                />

                <Pagination
                  pagination={pagination}
                  updateFilter={updateFilter}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <GlobalDeleteModal onDeleted={handleDelete} isDeleting={isDeleting} />
    </>
  );
}

export default List;
